import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, Search, Smartphone, Trash2 } from 'lucide-react';
import { useParams } from 'react-router-dom';
import {
  listChannels,
  listDeviceMappings,
  listKnownDevices,
  removeDeviceMapping,
  setDeviceChannel,
  setDeviceNickname,
} from '../api/AppOtaApi';
import DeviceSelect from '../components/DeviceSelect';
import { ActionDrawer, ConfirmDialog, Toast } from '../components/ui';
import styles from './AppUpdateDeviceIdMgr.module.scss';

function AppUpdateDeviceIdMgr() {
  const { packageName } = useParams();
  const [channels, setChannels] = useState([]);
  const [deviceMappings, setDeviceMappings] = useState([]);
  const [knownDevices, setKnownDevices] = useState([]);
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [newDeviceId, setNewDeviceId] = useState('');
  const [newDeviceChannel, setNewDeviceChannel] = useState('release');
  const [newDeviceNickname, setNewDeviceNickname] = useState('');
  const [editingNickname, setEditingNickname] = useState(null);
  const [editNicknameValue, setEditNicknameValue] = useState('');
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const fetchChannels = useCallback(async () => {
    try {
      const response = await listChannels(packageName);
      setChannels(response.data || []);
    } catch (error) {
      setErrorMessage(`Error fetching channels: ${error.message}`);
    }
  }, [packageName]);

  const fetchDeviceMappings = useCallback(async () => {
    try {
      const response = await listDeviceMappings(packageName);
      setDeviceMappings(response.data || []);
    } catch (error) {
      setErrorMessage(`Error fetching device assignments: ${error.message}`);
    }
  }, [packageName]);

  const fetchKnownDevices = useCallback(async () => {
    try {
      const response = await listKnownDevices();
      setKnownDevices(response.data || []);
    } catch (error) {
      setErrorMessage(`Error fetching known devices: ${error.message}`);
    }
  }, []);

  useEffect(() => {
    fetchChannels();
    fetchDeviceMappings();
  }, [fetchChannels, fetchDeviceMappings]);

  // Known devices are only needed by the assignment drawer; refresh them each time it opens
  useEffect(() => {
    if (showAddDevice) {
      fetchKnownDevices();
    }
  }, [showAddDevice, fetchKnownDevices]);

  const clearMessages = () => {
    setSuccessMessage('');
    setErrorMessage('');
  };

  const closeAddDrawer = () => {
    setShowAddDevice(false);
    setNewDeviceId('');
    setNewDeviceChannel('release');
    setNewDeviceNickname('');
  };

  const assignedDeviceIds = useMemo(
    () => new Set(deviceMappings.map(mapping => mapping.device_id)),
    [deviceMappings]
  );

  const selectedKnownDevice = useMemo(
    () => knownDevices.find(device => device.device_id === newDeviceId.trim()) || null,
    [knownDevices, newDeviceId]
  );

  const handlePickDevice = (deviceId, device) => {
    setNewDeviceId(deviceId);
    // Nicknames are shared across apps: show the existing one so it is not overwritten blindly
    setNewDeviceNickname(device?.nickname || '');
  };

  const handleAddDevice = async () => {
    if (!newDeviceId.trim() || !newDeviceChannel) {
      setErrorMessage('Enter a device ID and choose a channel.');
      return;
    }
    try {
      setLoading(true);
      clearMessages();
      await setDeviceChannel(packageName, newDeviceId.trim(), newDeviceChannel);
      const nickname = newDeviceNickname.trim();
      if (nickname && nickname !== (selectedKnownDevice?.nickname || '')) {
        await setDeviceNickname(newDeviceId.trim(), nickname);
      }
      setSuccessMessage('Device assignment created.');
      closeAddDrawer();
      await fetchDeviceMappings();
    } catch (error) {
      setErrorMessage(`Could not assign device: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDeviceChannel = async (deviceId, channelName) => {
    try {
      setLoading(true);
      clearMessages();
      await setDeviceChannel(packageName, deviceId, channelName);
      setSuccessMessage(`${deviceId} now follows ${channelName}.`);
      await fetchDeviceMappings();
    } catch (error) {
      setErrorMessage(`Could not update channel: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDevice = async deviceId => {
    try {
      setLoading(true);
      clearMessages();
      await removeDeviceMapping(packageName, deviceId);
      setSuccessMessage(`${deviceId} now uses the default release channel.`);
      setPendingRemoval(null);
      await fetchDeviceMappings();
    } catch (error) {
      setErrorMessage(
        `Could not remove assignment: ${error.response?.data?.detail || error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditNickname = (deviceId, currentNickname) => {
    setEditingNickname(deviceId);
    setEditNicknameValue(currentNickname || '');
  };

  const closeNicknameDrawer = () => {
    setEditingNickname(null);
    setEditNicknameValue('');
  };

  const handleSaveNickname = async () => {
    try {
      setLoading(true);
      clearMessages();
      await setDeviceNickname(editingNickname, editNicknameValue.trim() || null);
      setSuccessMessage('Device nickname updated.');
      closeNicknameDrawer();
      await fetchDeviceMappings();
    } catch (error) {
      setErrorMessage(
        `Could not update nickname: ${error.response?.data?.detail || error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredMappings = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return deviceMappings;
    }
    return deviceMappings.filter(mapping =>
      [mapping.device_id, mapping.nickname, mapping.channel_name]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(normalizedQuery))
    );
  }, [deviceMappings, query]);

  const nonReleaseAssignments = deviceMappings.filter(
    mapping => mapping.channel_name !== 'release'
  ).length;

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>App deployment · {packageName}</span>
          <h1>Device assignments</h1>
          <p>
            Override this app's release channel only for test, pilot, or customer-specific devices.
            Assignments are per app; nicknames are shared by every app on the device.
          </p>
        </div>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={() => setShowAddDevice(true)}
        >
          <Plus size={17} /> Assign device
        </button>
      </header>

      {successMessage && (
        <Toast tone="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Toast>
      )}
      {errorMessage && (
        <Toast tone="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Toast>
      )}

      <section className={styles.summary} aria-label="Assignment summary">
        <div>
          <span>Explicit assignments</span>
          <strong>{deviceMappings.length}</strong>
        </div>
        <div>
          <span>Non-release devices</span>
          <strong>{nonReleaseAssignments}</strong>
        </div>
        <div>
          <span>Available channels</span>
          <strong>{channels.length}</strong>
        </div>
      </section>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>Assigned devices</h2>
            <p>Unlisted devices automatically receive the release channel.</p>
          </div>
          <label className={styles.searchField}>
            <Search size={16} />
            <span className={styles.srOnly}>Search assigned devices</span>
            <input
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search ID, nickname, channel"
            />
          </label>
        </div>

        {filteredMappings.length ? (
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Channel</th>
                  <th>Target</th>
                  <th>
                    <span className={styles.srOnly}>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredMappings.map(mapping => (
                  <tr key={mapping.device_id}>
                    <td data-label="Device">
                      <div className={styles.deviceIdentity}>
                        <span className={styles.deviceIcon}>
                          <Smartphone size={16} />
                        </span>
                        <span>
                          <strong>{mapping.nickname || 'Unnamed device'}</strong>
                          <code>{mapping.device_id}</code>
                        </span>
                        <button
                          type="button"
                          className={styles.iconButton}
                          onClick={() => handleEditNickname(mapping.device_id, mapping.nickname)}
                          title="Edit nickname"
                        >
                          <Pencil size={15} />
                        </button>
                      </div>
                    </td>
                    <td data-label="Channel">
                      <select
                        value={mapping.channel_name}
                        onChange={event =>
                          handleUpdateDeviceChannel(mapping.device_id, event.target.value)
                        }
                        disabled={loading}
                        className={styles.channelSelect}
                      >
                        {channels.map(channel => (
                          <option key={channel.channel_name} value={channel.channel_name}>
                            {channel.channel_name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td data-label="Target">
                      <span className={styles.versionBadge}>v{mapping.latest_versioncode}</span>
                    </td>
                    <td className={styles.actions}>
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => setPendingRemoval(mapping.device_id)}
                        title="Remove explicit assignment"
                      >
                        <Trash2 size={15} />
                        <span>Reset</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={styles.emptyState}>
            <Smartphone size={26} />
            <strong>{query ? 'No devices match your search' : 'No explicit assignments'}</strong>
            <p>
              {query
                ? 'Try another ID, nickname, or channel.'
                : 'All devices currently follow the release channel.'}
            </p>
          </div>
        )}
      </section>

      <ConfirmDialog
        open={!!pendingRemoval}
        title={`Reset ${pendingRemoval} to release?`}
        description="The explicit channel assignment is removed and the device follows the release channel."
        confirmLabel="Reset to release"
        tone="primary"
        busy={loading}
        onConfirm={() => handleRemoveDevice(pendingRemoval)}
        onCancel={() => setPendingRemoval(null)}
      />

      <ActionDrawer
        open={showAddDevice}
        eyebrow="Device assignment"
        title="Assign a device"
        description="Create an exception to the default release channel."
        onClose={closeAddDrawer}
        closeDisabled={loading}
        footer={
          <>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={closeAddDrawer}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleAddDevice}
              disabled={loading || !newDeviceId.trim()}
            >
              {loading ? 'Assigning…' : 'Assign device'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          <div className={styles.field}>
            <label htmlFor="assign-device-id">Android device</label>
            <DeviceSelect
              inputId="assign-device-id"
              devices={knownDevices}
              value={newDeviceId}
              onChange={handlePickDevice}
              badgeFor={device => (assignedDeviceIds.has(device.device_id) ? 'assigned' : null)}
            />
            <small className={styles.fieldHint}>
              Search by device ID, nickname, hub serial, machine name or company. Type an ID that is
              not listed to add a brand-new device.
            </small>
          </div>
          <label>
            <span>Channel</span>
            <select
              value={newDeviceChannel}
              onChange={event => setNewDeviceChannel(event.target.value)}
            >
              {channels.map(channel => (
                <option key={channel.channel_name} value={channel.channel_name}>
                  {channel.channel_name} · v{channel.latest_versioncode}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>
              Nickname <small>optional · shared by every app on this device</small>
            </span>
            <input
              type="text"
              value={newDeviceNickname}
              onChange={event => setNewDeviceNickname(event.target.value)}
              placeholder="e.g. Berlin test tablet"
            />
          </label>
          <p className={styles.drawerHint}>
            {assignedDeviceIds.has(newDeviceId.trim())
              ? 'This device already has an assignment in this app; saving will move it to the selected channel.'
              : 'The assignment takes effect the next time this device checks for an update.'}
          </p>
        </div>
      </ActionDrawer>

      <ActionDrawer
        open={Boolean(editingNickname)}
        eyebrow="Device identity"
        title="Edit nickname"
        description={editingNickname || ''}
        onClose={closeNicknameDrawer}
        closeDisabled={loading}
        footer={
          <>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={closeNicknameDrawer}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleSaveNickname}
              disabled={loading}
            >
              {loading ? 'Saving…' : 'Save nickname'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          <label>
            <span>Nickname</span>
            <input
              type="text"
              value={editNicknameValue}
              onChange={event => setEditNicknameValue(event.target.value)}
              placeholder="Leave empty to remove nickname"
              autoFocus
            />
          </label>
          <p className={styles.drawerHint}>
            Nicknames only help operators identify a device; they do not change its device ID and
            apply to every app installed on it.
          </p>
        </div>
      </ActionDrawer>
    </div>
  );
}

export default AppUpdateDeviceIdMgr;
